import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { INVESTORS, COMPANY, DISCLAIMER } from "../data/content";
import { FILINGS, EDGAR_PROFILE_URL } from "../data/filings";

export default function Investors() {
  const latest = FILINGS.slice(0, 6);
  return (
    <div data-testid="page-investors">
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> {INVESTORS.eyebrow}
          </div>
          <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            {INVESTORS.headline}
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
            <p className="lg:col-span-6 text-[#F5F2EB] leading-relaxed">{INVESTORS.lead}</p>
            <p className="lg:col-span-6 text-[#F5F2EB] leading-relaxed">{INVESTORS.sub}</p>
          </div>
        </div>
      </section>

      {/* Company at a glance */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
          <span className="rule-orange" /> Market Information
        </div>
        <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight">
          Company at a glance
        </h2>
        <p className="mt-4 text-sm text-[#A79E93]" data-testid="market-note">
          Market information may be delayed and is provided for general reference only.
        </p>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 border border-[#DED6C6]" data-testid="market-grid">
          {INVESTORS.companyAtAGlance.map((c, i) => (
            <div
              key={c.label}
              className={`p-6 lg:p-8 ${i > 0 ? "border-l border-[#DED6C6]" : ""} ${
                i >= 2 ? "border-t lg:border-t-0" : ""
              }`}
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A79E93]">{c.label}</div>
              <div className="mt-3 font-display text-2xl lg:text-3xl text-[#14110E] font-semibold" data-testid={`market-${c.label.replace(/\s+/g,"-").toLowerCase()}`}>
                {c.value}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Chart + press */}
      <section className="bg-[#F4F1EA] border-y border-[#DED6C6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> Market Chart
            </div>
            <h3 className="mt-6 font-display text-2xl lg:text-3xl text-[#14110E] font-semibold">
              OTC:GCAN market quote
            </h3>
            <p className="mt-4 text-sm text-[#4A3D30]">
              Detailed price history is available via TradingView and the OTC Markets page for GCAN.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={INVESTORS.tradingViewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#14110E] text-[#14110E] hover:bg-[#14110E] hover:text-white px-5 py-3 text-sm font-semibold transition-colors"
                data-testid="investors-tradingview"
              >
                GCAN on TradingView <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={INVESTORS.pressReleasesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#14110E] text-[#14110E] hover:bg-[#14110E] hover:text-white px-5 py-3 text-sm font-semibold transition-colors"
                data-testid="investors-press-releases"
              >
                OTC Markets Press Releases <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white border border-[#DED6C6] p-7">
              <div className="eyebrow text-[#F26A1B]">Investor Contact</div>
              <h4 className="mt-4 font-display text-xl text-[#14110E] font-semibold">Investor Relations</h4>
              <p className="mt-3 text-sm text-[#4A3D30]">Direct correspondence for shareholder and prospective investor inquiries.</p>
              <a
                href={`mailto:${COMPANY.irEmail}`}
                className="mt-5 inline-flex items-center gap-2 text-[#14110E] font-semibold hover:text-[#F26A1B]"
                data-testid="investors-ir-email"
              >
                {COMPANY.irEmail} <ArrowUpRight className="h-4 w-4" />
              </a>

              <div className="mt-8 pt-6 border-t border-[#DED6C6]">
                <div className="eyebrow text-[#A79E93]">Transfer Agent</div>
                <p className="mt-3 text-sm text-[#14110E] font-semibold">{INVESTORS.transferAgent.name}</p>
                <p className="mt-1 text-sm text-[#4A3D30]">{INVESTORS.transferAgent.address}</p>
                <p className="mt-1 text-sm text-[#4A3D30] font-mono">Tel: {INVESTORS.transferAgent.phone}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest filings table */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> SEC Filings
            </div>
            <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight">
              Latest official filings
            </h2>
            <p className="mt-3 text-sm text-[#4A3D30] max-w-xl">
              The latest Company filings are loaded automatically from SEC EDGAR under CIK {COMPANY.cik}.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/sec-filings"
              className="inline-flex items-center gap-2 bg-[#14110E] hover:bg-[#1A1512] text-white px-5 py-3 text-sm font-semibold"
              data-testid="investors-view-all-filings"
            >
              View all filings <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={EDGAR_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#14110E] text-[#14110E] hover:bg-[#F4F1EA] px-5 py-3 text-sm font-semibold"
              data-testid="investors-edgar-profile"
            >
              SEC EDGAR profile <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="mt-10 border border-[#DED6C6] overflow-x-auto">
          <table className="w-full text-sm" data-testid="investors-filings-table">
            <thead className="bg-[#F4F1EA] text-[#4A3D30] font-mono text-[11px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left px-5 py-4 w-32">Form</th>
                <th className="text-left px-5 py-4 w-36">Filed</th>
                <th className="text-left px-5 py-4">Description</th>
                <th className="text-right px-5 py-4 w-36">Filing</th>
              </tr>
            </thead>
            <tbody>
              {latest.map((f, i) => (
                <tr key={i} className="border-t border-[#DED6C6] hover:bg-[#F4F1EA] transition-colors">
                  <td className="px-5 py-4 font-mono text-[#14110E] font-semibold">{f.form}</td>
                  <td className="px-5 py-4 font-mono text-[#4A3D30]">{f.filed}</td>
                  <td className="px-5 py-4 text-[#14110E]">{f.description}</td>
                  <td className="px-5 py-4 text-right">
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#F26A1B] hover:text-[#E63E1A] font-semibold"
                      data-testid={`investors-filing-link-${i}`}
                    >
                      View <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F4F1EA] border-t border-[#DED6C6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> Investor Resources
          </div>
          <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight">
            Corporate information
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6" data-testid="investors-faq">
            {INVESTORS.faqs.map((f, i) => (
              <div key={i} className="border-l-2 border-[#F26A1B] pl-5" data-testid={`investors-faq-${i}`}>
                <h4 className="font-display text-lg text-[#14110E] font-semibold">{f.q}</h4>
                <p className="mt-2 text-sm text-[#4A3D30] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-14 text-xs text-[#8A8075] leading-relaxed max-w-4xl">
            {DISCLAIMER.forwardLooking}
          </p>
        </div>
      </section>
    </div>
  );
}
